<template>
  <div ref="drop-zone" class="position-relative" @click="handleDropZoneClick">
    <v-card
      v-if="isOverDropZone"
      variant="tonal"
      class="d-flex flex-column w-full h-full position-absolute bg-surface"
      style="z-index: 1; inset: 0; border: 2px dashed rgb(var(--v-theme-primary)); border-radius: 8px"
    >
      <v-card-text class="h-full d-flex justify-center align-center">
        <div class="ma-auto text-body-medium text-center" variant="text">
          <v-card-item class="text-body-medium">
            <v-icon class="text-medium-emphasis" size="62" icon="upload_file" />
            <div class="mt-2 text-title-large">{{ $t("uploadFiles") }}</div>
          </v-card-item>
        </div>
      </v-card-text>
    </v-card>

    <v-card variant="text" v-bind="$attrs" class="d-flex flex-column" :class="{ border: bordered }">
      <v-list class="py-1 flex-shrink-0">
        <v-list-item class="pl-2 pr-2">
          <v-dialog v-if="!isReadOnly && !!fileCreateFunction && canCreateFiles" v-model="isDialogOpen" persistent width="600">
            <template #activator="{ props: dialogProps }">
              <v-btn
                v-if="$vuetify.display.smAndUp"
                v-bind="dialogProps"
                class="text-body-medium px-2 custom-hover-secondary"
                color="secondary"
                append-icon="add_circle"
                variant="tonal"
                @click.stop
              >
                {{ $t("addFiles") }}
              </v-btn>
              <v-btn
                v-else
                v-bind="dialogProps"
                class="text-body-medium custom-hover-secondary"
                color="secondary"
                variant="tonal"
                size="40"
                icon="add"
              />
            </template>

            <template #default>
              <v-card :title="$t('addFiles')">
                <add-file-form
                  multiple
                  :accept="accept"
                  :create-function="fileCreateFunction"
                  :delete-function="fileDeleteFunction"
                  @upload="
                    (newFiles) => {
                      files = [...newFiles, ...files]
                      fileIds = [...newFiles.map((file) => file._id), ...fileIds]
                    }
                  "
                  @delete="
                    (fileId) => {
                      files = files.filter((file) => file._id !== fileId)
                      fileIds = fileIds.filter((id) => id !== fileId)
                    }
                  "
                  @ok="() => (isDialogOpen = false)"
                />
              </v-card>
            </template>
          </v-dialog>

          <template #append>
            <v-btn-toggle v-model="viewType" variant="outlined" density="comfortable" mandatory @click.stop>
              <v-btn title="Grid view" class="px-5" icon="grid_view" value="grid" />
              <v-btn title="List view" class="px-5" icon="list" value="list" />
            </v-btn-toggle>
          </template>
        </v-list-item>
      </v-list>

      <v-divider />

      <v-card-text v-if="!fileIds.length">
        <v-card class="ma-auto text-body-medium text-center" variant="text">
          <v-card-item class="text-body-medium">
            <v-icon class="text-medium-emphasis" size="40" icon="image" />
            <div class="font-weight-medium">{{ $t("noFiles") }}</div>
            <div class="text-medium-emphasis">{{ $t("dragAndDropOrClickToUpload") }}</div>
          </v-card-item>
        </v-card>
      </v-card-text>

      <v-card-text v-else-if="viewType === 'grid'" class="overflow-auto">
        <div ref="fileCardGridElement" class="d-flex ga-3" :class="{ 'flex-wrap': wrap }">
          <file-item-card
            v-for="(file, index) of files"
            :key="file._id"
            class="flex-shrink-0"
            :model-value="file"
            :image-size="220"
            :movable="!!fileOrderFunction && !isReadOnly"
            :hide-delete="isReadOnly"
            :hide-remark="isReadOnly"
            :hide-rename="isReadOnly"
            :hide-rotate="isReadOnly"
            :hide-visibility="isReadOnly"
            :primary="!!primaryImageId && primaryImageId === file._id"
            @click.stop
            @open-image="openCarousel"
            @update:model-value="(newFileData) => (files[index] = newFileData)"
            @delete="() => deleteFile(file._id)"
          />
        </div>
      </v-card-text>

      <v-list v-else-if="viewType === 'list'">
        <div ref="fileItemListElement">
          <file-item
            v-for="(file, index) of files"
            :key="file._id"
            :model-value="file"
            :primary="!!primaryImageId && primaryImageId === file._id"
            :movable="!!fileOrderFunction && !isReadOnly"
            :hide-delete="isReadOnly"
            :hide-remark="isReadOnly"
            :hide-rename="isReadOnly"
            :hide-visibility="isReadOnly"
            class="px-2"
            @click.stop
            @update:model-value="(newFileData) => (files[index] = newFileData)"
            @delete="() => deleteFile(file._id)"
            @fullscreen="() => openCarousel(file._id)"
          />
        </div>
      </v-list>

      <template v-if="files.length && !hideFooter">
        <v-divider />
        <v-card-text class="py-1 d-flex justify-end text-body-small">
          {{ $t("fileCount", files.length) }}
        </v-card-text>
      </template>
    </v-card>

    <v-dialog v-model="isCarouselOpen" fullscreen>
      <template #default="{ isActive }">
        <v-card>
          <document-carousel
            v-if="isCarouselOpen"
            v-model="currentIndex"
            height="100%"
            :organization-id="organization"
            :documents="carouselDocuments"
            :pending="carouselPending"
            :readonly="isReadOnly"
            :is-primary="carouselDocuments[currentIndex]?.meta._id === primaryImageId"
            show-thumbnails
            :hide-primary-settings="!primaryImageId"
            @close="() => (isActive.value = false)"
            @update-document="(fileData) => updateDocumentFromCarousel(fileData, [files])"
            @remove-document="handleRemoveDocumentFromCarousel"
          />
        </v-card>
      </template>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { useSortable } from "@vueuse/integrations/useSortable"
import isEqual from "lodash-es/isEqual"

const fileIds = defineModel<Array<string>>("fileIds", { default: [] })

const props = withDefaults(
  defineProps<{
    organization: string
    isReadOnly?: boolean
    accept?: string
    wrap?: boolean
    hideFooter?: boolean
    square?: boolean
    bordered?: boolean
    fileCreateFunction?: (files: Array<File>) => Promise<Array<FileData>>
    fileDeleteFunction?: (fileId: string) => Promise<unknown>
    fileOrderFunction?: (fileIds: Array<string>) => Promise<Array<FileData>>
    onDropZoneClick?: () => void
    primaryImageId?: string
  }>(),
  {
    isReadOnly: false,
    accept: undefined,
    wrap: false,
    hideFooter: false,
    square: false,
    bordered: false,
    fileCreateFunction: undefined,
    fileDeleteFunction: undefined,
    fileOrderFunction: undefined,
    onDropZoneClick: undefined,
    primaryImageId: undefined,
  },
)
const { organization, isReadOnly, accept, fileCreateFunction, fileDeleteFunction, fileOrderFunction, onDropZoneClick, primaryImageId } =
  toRefs(props)

const $i18n = useI18n()

const fileStore = useFileStore()
const mainStore = useMainStore()
const authStore = useAuthStore()

const files = ref<Array<FileData>>([])
const {
  carouselDocuments,
  isCarouselOpen,
  carouselPending,
  currentIndex,
  openDocumentCarousel,
  removeDocumentFromCarousel,
  updateDocumentFromCarousel,
} = useDocumentCarousel()
const openCarousel = (fileId: string) => openDocumentCarousel(fileId, files.value)

const viewType = useLocalStorage<"grid" | "list">("file-view", "grid")

const dropZoneRef = useTemplateRef("drop-zone")

const canCreateFiles = authStore.hasAnyScope(organization.value, [AuthScope.CAN_CREATE_FILES])

const onDrop = async (droppedFiles: Array<File> | null) => {
  if (!droppedFiles) {
    return
  }

  if (!fileCreateFunction.value) {
    // Happens when component is not readonly and no create function is provided
    throw new Error("No file create function provided")
  }

  useExplicitSave(mainStore.notify, $i18n, async () => {
    const newFiles = await fileCreateFunction.value!(droppedFiles)
    files.value = [...newFiles, ...files.value]
    fileIds.value = [...newFiles.map((file) => file._id), ...fileIds.value]
  })
}

const isDialogOpen = ref(false)

const handleDropZoneClick = () => {
  if (onDropZoneClick.value) {
    onDropZoneClick.value()
    return
  }

  if (!isReadOnly.value && fileCreateFunction.value) {
    isDialogOpen.value = true
  }
}

const { isOverDropZone } = useDropZone(dropZoneRef, {
  onDrop,
  // specify the types of data to be received.
  dataTypes: ["image/jpeg", "image/png", "image/svg", "application/pdf"],
  // control multi-file drop
  multiple: true,
  // whether to prevent default behavior for unhandled events
  preventDefaultForUnhandled: false,
})

const deleteFile = (fileId: string) => {
  useExplicitSave(
    mainStore.notify,
    $i18n,
    async () => {
      if (!fileDeleteFunction.value) {
        // Happens when component is not readonly and no delete function is provided
        throw new Error("No file delete function provided")
      }

      await fileDeleteFunction.value(fileId)

      files.value = files.value.filter((file) => file._id !== fileId)
      fileIds.value = fileIds.value.filter((id) => id !== fileId)
    },
    "remove",
  )
}
const handleRemoveDocumentFromCarousel = (fileId: string) => removeDocumentFromCarousel(fileId, deleteFile)

watch(
  fileIds,
  async () => {
    const shouldFetchFiles = !isEqual(
      fileIds.value,
      files.value.map((file) => file._id),
    )

    if (shouldFetchFiles) {
      const fetchedFiles = await fileStore.getManyById(fileIds.value)

      files.value = fileIds.value
        .map((id) => fetchedFiles.find((file) => file._id === id))
        .filter((file) => file !== undefined) as Array<FileData>
    }
    // Ensure primary image is first
    if (primaryImageId.value) {
      const ids = files.value.map((file) => file._id)

      const primaryImageIndex = ids.indexOf(primaryImageId.value)

      if (primaryImageIndex > 0) {
        const primaryImage = files.value.splice(primaryImageIndex, 1)[0]
        if (primaryImage !== undefined) {
          files.value.unshift(primaryImage)
        }
      }
    }
  },
  { immediate: true },
)

let stopSortableGridInstance = () => {}
const fileCardGridElement = useTemplateRef("fileCardGridElement")

watch(fileCardGridElement, () => {
  if (!fileCardGridElement.value) {
    stopSortableGridInstance()
    return
  }

  const { stop } = useSortable(fileCardGridElement, files, {
    animation: 200,
    handle: ".handle",
    onUpdate: (e) => {
      const arrangedItems = moveArrayElement(files.value, e.oldIndex!, e.newIndex!)

      useExplicitSave(mainStore.notify, $i18n, async () => {
        if (!fileOrderFunction.value) {
          // Happens when component is not readonly and no order function is provided
          throw new Error("No file order function provided")
        }
        files.value = await fileOrderFunction.value(arrangedItems.map((g) => g._id))
      })
    },
  })
  stopSortableGridInstance = stop
})

let stopSortableListInstance = () => {}
const fileItemListElement = useTemplateRef("fileItemListElement")

watch(fileItemListElement, () => {
  if (!fileItemListElement.value) {
    stopSortableListInstance()
    return
  }

  const { stop } = useSortable(fileItemListElement, files, {
    animation: 200,
    handle: ".handle",
    onUpdate: (e) => {
      const arrangedItems = moveArrayElement(files.value, e.oldIndex!, e.newIndex!)

      useExplicitSave(mainStore.notify, $i18n, async () => {
        if (!fileOrderFunction.value) {
          // Happens when component is not readonly and no order function is provided
          throw new Error("No file order function provided")
        }

        files.value = await fileOrderFunction.value(arrangedItems.map((g) => g._id))
      })
    },
  })
  stopSortableListInstance = stop
})

onUnmounted(() => {
  stopSortableGridInstance()
  stopSortableListInstance()
})
</script>
